import { ReactElement, useCallback, useMemo, useEffect } from 'react';
import { Typography, Stack, TextField, DialogActions } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useFormik, FormikHelpers } from 'formik';

import { updateTemplateName } from '~services/templateRoute';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { hasError, getHelperText } from '~utils/formHelpers';
import { formatId } from '~utils/formatter';

import { DialogChangeTemplateNameProps } from './types';
import { FIELDS_NAME, getInitialValues, validationSchema } from './utils';
import {
  DialogChangeTemplateNameContainer,
  DialogChangeTemplateNameTitle,
  DialogChangeTemplateNameTitleCloseIcon,
  DialogChangeTemplateNameContent,
} from './styles';

const DialogChangeTemplateName = ({
  open,
  onClose,
  onConfirmSuccess,
  routeTemplateId,
  defaultName,
}: DialogChangeTemplateNameProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingUpdateTemplateName, , executeUpdateTemplateName] =
    useLazyRequest({
      request: updateTemplateName,
      withPostSuccess: () => {
        const message = `El nombre de la plantilla #${formatId(
          routeTemplateId,
        )} fue cambiado correctamente`;

        enqueueSnackbar(message, { variant: 'success' });

        onConfirmSuccess?.();
      },
      withPostFailure: () => {
        enqueueSnackbar(
          'No se pudo cambiar el nombre de la plantilla, intente nuevamente',
          { variant: 'error' },
        );
      },
    });

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      await executeUpdateTemplateName({
        routeTemplateId,
        routeTemplateName: values[FIELDS_NAME.NAME],
      });

      setSubmitting(false);
    },
    [executeUpdateTemplateName, routeTemplateId],
  );

  const {
    resetForm,
    submitForm,
    isSubmitting,
    getFieldProps,
    touched,
    errors,
    dirty,
    isValid,
  } = useFormik({
    initialValues: getInitialValues(defaultName),
    validationSchema,
    onSubmit,
  });

  const disabledSubmit = useMemo(() => !dirty || !isValid, [dirty, isValid]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DialogChangeTemplateNameContainer open={open} maxWidth="md">
      <DialogChangeTemplateNameTitle>
        <Typography component="h2" variant="h6">
          Cambiar nombre de plantilla
        </Typography>

        <DialogChangeTemplateNameTitleCloseIcon
          aria-label="close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </DialogChangeTemplateNameTitleCloseIcon>
      </DialogChangeTemplateNameTitle>

      <DialogChangeTemplateNameContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Nuevo nombre de plantilla"
            autoComplete="off"
            fullWidth
            error={hasError(touched, errors, FIELDS_NAME.NAME)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.NAME)}
            {...getFieldProps(FIELDS_NAME.NAME)}
          />
        </Stack>
      </DialogChangeTemplateNameContent>

      <DialogActions>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loadingUpdateTemplateName || isSubmitting}
          disabled={disabledSubmit}
        >
          Guardar
        </LoadingButton>
      </DialogActions>
    </DialogChangeTemplateNameContainer>
  );
};

export default DialogChangeTemplateName;
